/**
 * 配信先リストの CSV / Excel 書き出し。
 * 検索フィルター適用中は「表示中のリスト」をそのまま書き出す（呼び出し側が filtered を渡す）。
 * 列名は ContactImportDialog の自動検出ヒントに合わせ、再取り込み互換にしている。
 * 仕様書: docs/01_spec/spec_mail_system.md B-10.3
 */
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import type { MailContactRecord } from '@/hooks/useMailContacts';
import type { MailGroupRecord } from '@/hooks/useMailGroups';
import { formatDate } from '@/lib/firestoreTime';

const PROMOTION_LABEL: Record<string, string> = {
  lead: 'リード',
  customer: '顧客',
  inactive: '休眠',
};

const FIXED_HEADERS = [
  '氏名',
  '姓',
  '名',
  '姓（カナ）',
  '名（カナ）',
  'メールアドレス',
  '追加メールアドレス',
  '会社名',
  '電話番号',
  '職業',
  '性別',
  '生年月日',
  '郵便番号',
  '都道府県',
  '市区町村',
  '町名・番地',
  'タグ',
  'グループ',
  'ステータス',
  '登録日',
  '更新日',
] as const;

export function buildContactsExportTable(
  contacts: MailContactRecord[],
  groupMap: Map<string, MailGroupRecord>,
): { headers: string[]; rows: string[][] } {
  // カスタムフィールド列: 出現順で収集（固定列と同名のキーは固定列を優先して除外）
  const fixedSet = new Set<string>(FIXED_HEADERS);
  const customKeys: string[] = [];
  for (const c of contacts) {
    for (const k of Object.keys(c.customFields ?? {})) {
      if (!fixedSet.has(k) && !customKeys.includes(k)) customKeys.push(k);
    }
  }

  const headers = [...FIXED_HEADERS, ...customKeys];
  const rows = contacts.map((c) => {
    const extraEmails = (c.emails ?? [])
      .map((e) => e.address)
      .filter((a) => a && a !== c.defaultEmail);
    const groupNames = (c.groupIds ?? [])
      .map((gid) => groupMap.get(gid)?.name)
      .filter((n): n is string => !!n);
    const addr = c.personalAddress;
    const row: string[] = [
      c.name ?? '',
      c.lastName ?? '',
      c.firstName ?? '',
      c.lastNameKana ?? '',
      c.firstNameKana ?? '',
      c.defaultEmail ?? '',
      extraEmails.join('; '),
      c.companyName ?? '',
      c.phone ?? '',
      c.occupation ?? '',
      c.gender ?? '',
      c.birthdate ?? '',
      addr?.postalCode ?? '',
      addr?.prefecture ?? '',
      addr?.city ?? '',
      addr?.line ?? '',
      (c.tags ?? []).join('; '),
      groupNames.join('; '),
      PROMOTION_LABEL[c.promotionLevel ?? 'lead'] ?? '',
      formatDate(c.createdAt),
      formatDate(c.updatedAt),
    ];
    for (const k of customKeys) {
      const v = c.customFields?.[k];
      row.push(typeof v === 'string' ? v : v == null ? '' : String(v));
    }
    return row;
  });

  return { headers, rows };
}

function exportFileStamp(): string {
  return new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportContactsCsv(
  contacts: MailContactRecord[],
  groupMap: Map<string, MailGroupRecord>,
) {
  const { headers, rows } = buildContactsExportTable(contacts, groupMap);
  const csv = Papa.unparse({ fields: headers, data: rows });
  // Excel での文字化け防止のため UTF-8 BOM を付与
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, `mail_contacts_${exportFileStamp()}.csv`);
}

export function exportContactsXlsx(
  contacts: MailContactRecord[],
  groupMap: Map<string, MailGroupRecord>,
) {
  const { headers, rows } = buildContactsExportTable(contacts, groupMap);
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  // 列幅を内容に応じて自動調整（10〜60 文字）
  ws['!cols'] = headers.map((h, ci) => {
    const maxLen = rows.reduce(
      (m, r) => Math.max(m, String(r[ci] ?? '').length),
      h.length,
    );
    return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '配信先');
  XLSX.writeFile(wb, `mail_contacts_${exportFileStamp()}.xlsx`);
}
